import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import { useState, ChangeEvent, useEffect } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers';
import BaranDataGrid from '../common/BaranDataGrid';
import { filterList } from '../../utils/array';
import IResponse, { IPagination } from '../../interfaces/response';
import axios from 'axios';
import { IInvoice } from '../../interfaces/finance';
import { Box, IconButton } from '@mui/material';
import { exportToExcel } from '../../utils/file';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

export default function InvoiceListTable() {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [fetchedData, setFetchedData] = useState<IInvoice[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(false);
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);

	const headerClassNames = 'text-xs bg-background-paper';

	const navigate = useNavigate();

	const { t } = useTranslation();

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const handleViewPage = (pageId: string) => () => {
		navigate(`/finance/invoices/${pageId}`);
	};

	const properties: (keyof IInvoice)[] = ['title'];

	const filteredRows = filterList(fetchedData, properties, searchTerm);
	const handleExportExcel = () => {
		exportToExcel({ data: filteredRows, fileName: 'invoice_list', t });
	};

	const columns: GridColDef<IInvoice>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{
			field: 'title',
			headerName: t('title'),
			width: 350,
			headerAlign: 'left',
			flex: 1,
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'total_price',
			headerName: t('amount'),
			width: 250,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
			renderCell: params => {
				const value = params.row.total_price + '+';
				return (
					<span className="text-green-600" style={{ direction: 'rtl' }}>
						{value}
					</span>
				);
			},
		},
		{
			field: 'created_at',
			headerName: t('date'),
			width: 150,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 182,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return (
					<>
						<IconButton aria-label="eye" onClick={handleViewPage(params.row.id.toString())}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	const getServer = () => {
		setLoading(true);
		axios<IResponse<IInvoice[]>>({
			url: '/accounting-profile/invoices',
			params: {
				page: page,
				limit: pageSize,
				search: searchTerm,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		getServer();
	}, [page, pageSize]);

	useEffect(() => {
		if (page === 1) {
			getServer();
		} else {
			setPage(1);
		}
	}, [search]);

	return (
		<Box className="w-full">
			<BaranDataGrid<IInvoice> pagination={pagination} loading={loading} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={fetchedData.length ? handleExportExcel : undefined} actionButtonLable={t('export_excel')} searchableFields={properties} />;
		</Box>
	);
}
